export function parseDateAsLocal(value: string | Date | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const dateStr = typeof value === 'string' ? value.split('T')[0] : null;
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
}

export function formatDateAsYyyyMmDd(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDateAsUtcMidnight(value: string | Date | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    return new Date(`${year}-${month}-${day}T00:00:00.000Z`);
  }
  const dateStr = typeof value === 'string' ? value.split('T')[0] : null;
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  return new Date(`${dateStr}T00:00:00.000Z`);
}

export function formatDateForApiResponse(value: Date | string | null): string | null {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.split('T')[0];
  }
  const d = new Date(value);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const DATE_TIME_PLACEHOLDERS = new Set(['', '--', '-', 'N/A', 'n/a', 'null', 'undefined']);

/**
 * Split a datetime value such as `2026-08-11 18:00:00` into date and time parts.
 * Timezone-less `YYYY-MM-DD HH:mm:ss` strings are split as-is (no timezone conversion).
 */
export function splitDateTimeParts(
  value: string | Date | null | undefined,
  emptyPlaceholder = '--'
): { date: string; time: string } {
  if (value == null) {
    return { date: emptyPlaceholder, time: emptyPlaceholder };
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || DATE_TIME_PLACEHOLDERS.has(trimmed)) {
      const display = !trimmed
        ? emptyPlaceholder
        : trimmed.toLowerCase() === 'n/a'
          ? 'N/A'
          : trimmed === 'null' || trimmed === 'undefined'
            ? emptyPlaceholder
            : trimmed;
      return { date: display, time: display };
    }

    const hasExplicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
    const localMatch = trimmed.match(
      /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?$/
    );
    if (localMatch && !hasExplicitZone) {
      const time = localMatch[2].length === 5 ? `${localMatch[2]}:00` : localMatch[2];
      return { date: localMatch[1], time };
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return { date: trimmed, time: emptyPlaceholder };
    }
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (isNaN(parsed.getTime())) {
    return { date: emptyPlaceholder, time: emptyPlaceholder };
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
    time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`,
  };
}
